import { type MCPServerInfo } from '../../types';

const CnaJutroDeveloperAssistantServer: MCPServerInfo = {
  serverId: 'CNA_JUTRO_DEVELOPER_ASSISTANT',
  name: 'Jutro Developer Assistant',
  slug: 'jutro-developer-assistant',
  description:
    'Documentation and component assistant for the Guidewire Jutro Digital Platform — Design System, Digital SDK, patterns and templates. Runs locally and connects to the Jutro Developer Assistant API.',
  authenticationType: 'STATIC_CREDS',
  source: {
    sourceType: 'LOCAL_PACKAGE',
    transport: 'STDIO',
    // @gwmcp/jutro-dev-assistant is distributed through Guidewire's private npm
    // registry — npx -y would attempt to fetch from the public npmjs.com registry
    // and fail for users whose npm is not configured for it. The binary is expected
    // to be installed globally (npm install -g @gwmcp/jutro-dev-assistant) and exposed
    // as the `jutro-dev-assistant` command.
    commandFunction: `(config) => ({
  command: 'jutro-dev-assistant',
  args: [],
  env: {
    // Trust the OS certificate store when reaching *.guidewire.net APIs.
    NODE_OPTIONS: '--use-system-ca',
    // Each value is only injected when the user provided it — nothing is baked
    // in. Natoma sends the dev key as GW_MCP_JWT_TOKEN, the highest-priority
    // credential in the Jutro SDK auth chain (sent as Authorization: Bearer).
    ...(config.devkey ? { GW_MCP_JWT_TOKEN: config.devkey } : {}),
    ...(config.oktaDomain ? { OKTA_DOMAIN: config.oktaDomain } : {}),
    ...(config.oktaClientId ? { OKTA_CLIENT_ID: config.oktaClientId } : {}),
    ...(config.devassistantServiceUrl ? { DEVASSISTANT_SERVICE_URL: config.devassistantServiceUrl } : {}),
  }
})`
  },
  serverConfig: {
    required: [],
    properties: {}
  },
  installConfig: {
    required: [],
    properties: {
      devkey: {
        type: 'string',
        description:
          'Personal dev key from Guidewire Home → Apps → Developer Assistant → "Show key".',
        sensitive: true
      },
      devassistantServiceUrl: {
        type: 'string',
        description:
          'Developer Assistant API base URL for your tenant. Copy the "Service URL" shown in Guidewire Home → Apps → Developer Assistant → "Show key".',
        sensitive: false,
        default: 'https://devassistant.api.omega2-andromeda.guidewire.net'
      },
      oktaClientId: {
        type: 'string',
        description:
          'OKTA_CLIENT_ID — Required for Okta browser sign-in; not needed if a dev key is provided.',
        sensitive: false
      },
      oktaDomain: {
        type: 'string',
        description:
          'OKTA_DOMAIN — Required for Okta browser sign-in; not needed if a dev key is provided. e.g. https://guidewire-hub.okta.com/oauth2/<id>.',
        sensitive: false
      }
    }
  },
  instructions: {
    installSetupInstructions: `## Prerequisites

- Membership in the \`gwa.any_planet_class.codelift.user\` group.
- npm access to Guidewire's Binary Repository (the \`@gwmcp\` scope).

## 1. Install

Point the \`@gwmcp\` scope at the Binary Repository for your region (\`amer\`, \`emea\`, or \`apac\`), then install:

\`\`\`bash
npm config set @gwmcp:registry https://{region}.binrepo.guidewire.net/artifactory/api/npm/mcp-npm-release-local
npm login --scope=@gwmcp --registry=https://{region}.binrepo.guidewire.net/artifactory/api/npm/mcp-npm-release-local
npm install -g @gwmcp/jutro-dev-assistant@latest
\`\`\`

## 2. Authenticate

Open **Guidewire Home → Apps → Developer Assistant → "Show key"** to get your **devkey** and **Service URL**, then choose one:

- **Devkey (no browser):** paste the devkey and Service URL into the install form.
- **Okta sign-in:** leave the devkey blank and enter your region's Okta domain and client ID (below). A browser window opens to sign in.

Okta values by region (for Okta sign-in):

- **AMER** — Domain \`https://guidewire-hub.okta.com/oauth2/aus11vix3uKEpIfSI357\`, Client ID \`0oa1i0wjjfaemaC8n358\`
- **APAC** — Domain \`https://guidewire-hub-apac.okta.com/oauth2/ausbg05gfcTZQ7bpH3l6\`, Client ID \`0oa5xoe3bxxW9nBus3l7\`
- **EMEA** — Domain \`https://guidewire-hub-eu.okta.com/oauth2/ausc2q01c40dNZII0416\`, Client ID \`0oalx602whsJdfH77417\`

## 3. Initialize

In each Jutro project, run \`/jutro-developer-assistant:setup claude\`.
`
  }
};

export default CnaJutroDeveloperAssistantServer;
